import { useSelector } from "react-redux";
import {
  selectSubGroupInvitations,
  selectSubGroupUsers,
} from "app/slices/admin.slice";
import Popover from "common/Popover/Popover";
import Table from "common/Table/Table";
import { formatDate, sortRawDates } from "@shared/utils/date";
import React, { useState } from "react";
import ActionsDropdown from "features/admin/group/invitations/ActionsDropdown";
import {
  FiltersWrapper,
  StatusWrapper,
} from "features/admin/group/users/styles";
import {
  SUB_GROUP_INVITATION_ACTIVE_STATUS,
  SUB_GROUP_INVITATION_INACTIVE_STATUS,
} from "common/constants";
import { blue, green, orange } from "@ant-design/colors";
import InvitationUsers from "features/admin/group/invitations/InvitationUsers";
import { toggleArrayItem } from "utils/arrays";
import { motion } from "motion/react";
import useMeasure from "utils/hooks/useMeasure";
import { StatusCircle } from "Global/StatusCircle";
import { sharedLib } from "@shared/services/shared.service";
import {
  ACTIVATE_INVITATION_CACHE_KAY,
  DEACTIVATE_INVITATION_CACHE_KAY,
  DELETE_INVITATION_CACHE_KAY,
} from "features/admin/constants";
import { useBottomNavigator } from "utils/hooks/misc.js";
import GroupSelect from "features/admin/group/GroupSelect.js";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import styled, { css, useTheme } from "styled-components";
import { ChevronRight } from "lucide-react";
import SortSelect from "common/Select/SortSelect";
import { SortingOrder } from "@shared/types";
import { phoneStackedRows } from "features/admin/NewLook/phoneRows";
import PanelTable from "features/admin/NewLook/PanelTable";
import TablePanel from "features/admin/NewLook/TablePanel";
import StatusDot from "features/admin/NewLook/StatusDot";
import TableSkeleton from "features/admin/NewLook/TableSkeleton";
import Pill from "common/NewLook/Pill.tsx";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

const Invitations = ({ subGroupID, isFetching, mobileActions }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const theme = useTheme();
  const { isNewLook, colors } = theme;
  const tokens = getNewLookTokens(theme);
  let subGroupMembers = useSelector(selectSubGroupUsers(subGroupID));
  let subGroupInvitations = useSelector(selectSubGroupInvitations(subGroupID));
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  // New look (phones): stacked rows; the "Created" sort moves to a sort pill.
  const isNewLookPhone = isNewLook && isBottomNavigator;
  const [phoneSortDirection, setPhoneSortDirection] = useState(
    SortingOrder.DESCEND
  );

  const deleteInvitationStatus =
    sharedLib.sharedApi.admin.useDeleteInvitationMutation({
      fixedCacheKey: DELETE_INVITATION_CACHE_KAY,
    })[1];
  const activateInvitationStatus =
    sharedLib.sharedApi.admin.useActivateInvitationMutation({
      fixedCacheKey: ACTIVATE_INVITATION_CACHE_KAY,
    })[1];
  const deactivateInvitationStatus =
    sharedLib.sharedApi.admin.useDeactivateInvitationMutation({
      fixedCacheKey: DEACTIVATE_INVITATION_CACHE_KAY,
    })[1];

  const isTableLoading =
    deleteInvitationStatus.isLoading ||
    activateInvitationStatus.isLoading ||
    deactivateInvitationStatus.isLoading;

  // New look: theme colours - green while it can be used, grey once it's
  // used up, orange when switched off.
  const getInvitationStatusAndColor = (invitation) => {
    if (
      !!invitation.num_users &&
      invitation.accepted_by.length >= invitation.num_users
    ) {
      return [
        t("invitations.statuses.fullyUsed"),
        isNewLook ? tokens.muted : blue[5],
      ];
    } else if (invitation.status === SUB_GROUP_INVITATION_ACTIVE_STATUS) {
      return [
        t("invitations.statuses.active"),
        isNewLook ? tokens.up : green[5],
      ];
    } else if (invitation.status === SUB_GROUP_INVITATION_INACTIVE_STATUS) {
      return [
        t("invitations.statuses.inactive"),
        isNewLook ? tokens.stop : orange[5],
      ];
    }
  };

  const columns = [
    {
      title: t("invitations.table.created"),
      dataIndex: "creation_datetime",
      key: "creation_datetime",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      width: isNewLook ? 200 : 160,
      sorter: (a, b) => sortRawDates(a.creation_datetime, b.creation_datetime),
      render: (creationDatetime, invitation) => {
        if (isNewLook)
          return (
            <Popover content={formatDate(creationDatetime, true)}>
              <CreatedCell>
                {formatDate(creationDatetime, false)}
                {invitation.is_new && <Pill>{t("invitations.table.new")}</Pill>}
              </CreatedCell>
            </Popover>
          );
        return (
          <Popover content={formatDate(creationDatetime, true)}>
            {formatDate(creationDatetime, false)}
            {invitation.is_new && (
              <span
                style={{
                  marginLeft: "8px",
                  // New look: the theme green, no heavier than 600.
                  color: isNewLook ? colors.green : "#389e0d",
                  fontSize: isNewLook ? "11px" : "10px",
                  fontWeight: isNewLook ? "600" : "700",
                }}
              >
                {t("invitations.table.new")}
              </span>
            )}
          </Popover>
        );
      },
    },
    {
      title: t("invitations.table.usernameTelegramId"),
      dataIndex: "telegram_user_id",
      key: "telegram_user_id",
      render: (_, invitation) =>
        isNewLook && !invitation.telegram_user_id && !invitation.username ? (
          <Muted>{t("invitations.table.all")}</Muted>
        ) : (
          invitation.telegram_user_id ||
          invitation.username ||
          t("invitations.table.all")
        ),
    },
    {
      title: t("invitations.table.duration"),
      dataIndex: "subscription_duration",
      key: "subscription_duration",
      render: (duration) =>
        duration
          ? duration + " " + t("invitations.table.days")
          : t("users.actions.lifetime"),
    },
    {
      title: t("invitations.table.acceptedPermitted"),
      dataIndex: "num_used",
      key: "num_used",
      // New look: a count, so it lines up on the right.
      align: isNewLook && !isBottomNavigator ? "right" : undefined,
      render: (_, invitation) => {
        return (
          <div>
            {invitation.accepted_by.length} /{" "}
            {invitation.num_users || t("invitations.table.unlimited")}
          </div>
        );
      },
    },
    {
      title: t("invitations.table.status"),
      dataIndex: "status",
      key: "status",
      render: (_, invitation) => {
        const [displayName, color] = getInvitationStatusAndColor(invitation);
        if (isNewLook)
          return <StatusDot color={color}>{displayName}</StatusDot>;
        return (
          <StatusWrapper>
            <StatusCircle color={color} key={invitation.id} />
            <span>{displayName}</span>
          </StatusWrapper>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: isNewLook ? 56 : 60,
      render: (_, invitation) => (
        <ActionsDropdown subGroupID={subGroupID} invitation={invitation} />
      ),
    },
  ];

  if (isNewLookPhone) {
    columns.forEach((column) => {
      column.className = `nl-${column.key}`;
      if (column.key === "creation_datetime") {
        column.sortOrder =
          phoneSortDirection === SortingOrder.ASCEND ? "ascend" : "descend";
      }
      if (column.key === "subscription_duration" || column.key === "num_used") {
        column.onCell = () => ({ "data-label": column.title });
      }
    });
  }

  subGroupInvitations = subGroupInvitations.map((invitation) => {
    return {
      ...invitation,
      key: invitation.id,
    };
  });

  const telegramIdToMembershipMapping = subGroupMembers.reduce(
    (mapping, member) => {
      mapping[member.telegram_user_id] = member;
      return mapping;
    },
    {}
  );

  const rowClassName = (invitation) => {
    let className = "";
    if (invitation.is_new) {
      className += "invitation-is-new ";
    }
    if (invitation.accepted_by.length > 0) {
      className += "cursor-pointer ";
    }
    if (expandedRowKeys.indexOf(invitation.id) >= 0) {
      className += "expanded";
    }
    return className;
  };

  const toggleRow = (invitation) =>
    invitation.accepted_by.length > 0 &&
    setExpandedRowKeys(toggleArrayItem(expandedRowKeys, invitation.id));

  const expandable = {
    expandedRowRender: (invitation) => (
      <AnimatedExpandingRow
        invitation={invitation}
        expanded={expandedRowKeys.indexOf(invitation.id) >= 0}
        telegramIdToMembershipMapping={telegramIdToMembershipMapping}
      />
    ),
    rowExpandable: (invitation) => invitation.accepted_by.length > 0,
    onExpandedRowsChange: (updatedExpandedRowKeys) =>
      setExpandedRowKeys(updatedExpandedRowKeys),
    expandedRowKeys: expandedRowKeys,
  };

  // New look: rows that open are buttons (Enter or Space toggles them) and the
  // expand control is a chevron that turns.
  if (isNewLook) {
    // Rows shaped like invitations while the first load runs.
    const showSkeleton = isFetching && !subGroupInvitations.length;
    const table = (
      <Table
        loading={!showSkeleton && (isTableLoading || isFetching)}
        columns={columns}
        dataSource={subGroupInvitations}
        rowClassName={rowClassName}
        expandable={{
          ...expandable,
          columnWidth: 52,
          expandIcon: ({ expanded, expandable: canExpand }) =>
            canExpand ? (
              <ExpandChevron $expanded={expanded} aria-hidden={true}>
                <ChevronRight size={16} strokeWidth={1.75} />
              </ExpandChevron>
            ) : null,
        }}
        onRow={(invitation) => {
          const canExpand = invitation.accepted_by.length > 0;
          return canExpand
            ? {
                tabIndex: 0,
                role: "button",
                "aria-expanded": expandedRowKeys.indexOf(invitation.id) >= 0,
                onClick: () => toggleRow(invitation),
                onKeyDown: (event) => {
                  if (
                    event.target === event.currentTarget &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    toggleRow(invitation);
                  }
                },
              }
            : {};
        }}
        locale={{
          emptyText: showSkeleton ? (
            <TableSkeleton />
          ) : (
            <PanelEmpty
              title={t("invitations.empty.title")}
              description={t("invitations.empty.description")}
            />
          ),
        }}
        itemsType={t("invitations.itemsType")}
      />
    );
    const panel = (
      <TablePanel
        title={t("users.tabs.invitations")}
        count={subGroupInvitations.length || undefined}
        flush={true}
      >
        {isNewLookPhone ? (
          <>
            {!!subGroupInvitations.length && (
              <PhoneSortRow>
                <SortSelect
                  sortBy="creation_datetime"
                  setSortBy={() => {}}
                  sortDirection={phoneSortDirection}
                  setSortDirection={setPhoneSortDirection}
                  sortValues={["creation_datetime"]}
                  labelMapper={() => t("invitations.table.created")}
                />
              </PhoneSortRow>
            )}
            <PhoneRows>{table}</PhoneRows>
          </>
        ) : (
          <NewLookTable>{table}</NewLookTable>
        )}
      </TablePanel>
    );
    if (!isBottomNavigator) return panel;
    return (
      <>
        <FiltersWrapper>
          <GroupSelect size="middle" />
        </FiltersWrapper>
        <NewLookPhoneContent>
          {mobileActions}
          {panel}
        </NewLookPhoneContent>
      </>
    );
  }

  return (
    <>
      {isBottomNavigator && (
        <FiltersWrapper>
          <GroupSelect size="middle" />
        </FiltersWrapper>
      )}
      <div style={{ padding: isBottomNavigator ? 12 : 0 }}>
        {isBottomNavigator && mobileActions}
        <Table
          loading={isTableLoading || isFetching}
          columns={columns}
          dataSource={subGroupInvitations}
          rowClassName={rowClassName}
          expandable={expandable}
          onRow={(invitation) => {
            return {
              onClick: () => toggleRow(invitation),
            };
          }}
          itemsType={t("invitations.itemsType")}
        />
      </div>
    </>
  );
};

export default React.memo(Invitations);

const PhoneSortRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const NewLookPhoneContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px 16px;
`;
// New look: the accepted members open in a well under their invitation.
const NewLookTable = styled(PanelTable)`
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-expanded-row > td {
    height: auto;
    padding: 0;
    background: ${(p) => getNewLookTokens(p.theme).well} !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row.expanded > td {
    border-bottom-color: transparent;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr.ant-table-row-level-0
    > td.ant-table-row-expand-icon-cell {
    padding-right: 0;
  }
`;
const ExpandChevron = styled.span`
  display: inline-flex;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  transition: transform 150ms;
  ${(p) =>
    p.$expanded &&
    css`
      transform: rotate(90deg);
    `}
`;
const CreatedCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-weight: 500;
  white-space: nowrap;
`;
const Muted = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
// New look (phones): created and status on top, then the invitee, the
// duration and the accepted/permitted count as detail lines.
const PhoneRows = styled.div`
  ${phoneStackedRows};
  .ant-table-tbody > tr.ant-table-row {
    grid-template-columns: 24px minmax(0, 1fr) auto 32px;
    grid-template-areas:
      "expand created status action"
      "expand user user user"
      "expand duration duration duration"
      "expand used used used";
    padding: 12px 8px 12px 12px;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  .ant-table-tbody > tr.ant-table-row:last-child {
    border-bottom: 0;
  }
  .ant-table-tbody > tr.ant-table-expanded-row > td {
    padding: 0 !important;
    background: ${(p) => getNewLookTokens(p.theme).well} !important;
  }
  td.ant-table-row-expand-icon-cell {
    grid-area: expand;
    align-self: start;
    padding-top: 2px !important;
  }
  td.nl-creation_datetime {
    grid-area: created;
  }
  td.nl-telegram_user_id {
    grid-area: user;
    ${newLookType.meta};
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
  td.nl-subscription_duration {
    grid-area: duration;
  }
  td.nl-num_used {
    grid-area: used;
  }
  td.nl-status {
    grid-area: status;
  }
  td.nl-action {
    grid-area: action;
    align-self: start;
    margin-top: -6px;
    text-align: right;
  }
  td[data-label] {
    min-height: 28px;
    color: ${(p) => getNewLookTokens(p.theme).text};
  }
  td[data-label]::before {
    ${newLookType.label};
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
`;

const AnimatedExpandingRow = ({
  invitation,
  expanded,
  telegramIdToMembershipMapping,
}) => {
  const [expandedRowRef, { height }] = useMeasure();

  const invitationMemberships = invitation.accepted_by
    .map((user) => telegramIdToMembershipMapping[user.telegram_user_id])
    .filter((member) => !!member);

  return (
    <motion.div
      initial={false}
      animate={{
        height: expanded ? height + 28 : 0,
        opacity: expanded ? 1 : 0,
      }}
      transition={{ type: "spring", stiffness: 170, damping: 26 }}
    >
      <div style={{ width: "100%", height: "100%", position: "absolute" }}>
        <InvitationUsers
          users={invitationMemberships}
          wrapperRef={expandedRowRef}
          telegramIdToMembershipMapping={telegramIdToMembershipMapping}
        />
      </div>
    </motion.div>
  );
};
